import {
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WorkspaceService } from '../../root-services/workspace.service';
import { ViewportService } from '../../root-services/viewport.service';
import { RecentChat } from '../../api/models/session.model';
import { WorkspaceSidebarStateService } from './workspace-sidebar-state.service';
import { WorkspaceRecentChatItemComponent } from './workspace-recent-chat-item.component';
import { IconComponent } from '../../shared/components/icons';
import { groupByTimePeriod } from '../../shared/utils/date-grouping.util';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { deleteChatDialogConfig } from '../../shared/components/confirmation-dialog/delete-chat-dialog.config';
import { NavigationContextService } from '../../root-services/navigation-context.service';
import { WorkspaceSessionsStateService } from '../../root-services/workspace-sessions-state.service';
import { AppScrollService } from '../../root-services/app-scroll.service';
import { extractErrorMessage } from '../../core/utils/error-message-extractor.util';
import {
  SIDEBAR_WIDTHS,
  SIDEBAR_WIDTH_STYLES,
} from './workspace-sidebar.const';
import { SIDEBAR_MAX_CHATS } from '../../shared/constants/workspace-chats.const';

/**
 * WorkspaceSidebarComponent
 *
 * Global workspace navigation sidebar providing:
 * - Workspace home navigation
 * - Workspace settings navigation
 * - Recent chats cross-agent list
 *
 * Features:
 * - Route-driven expansion (no manual toggle on desktop)
 * - Auto-collapse on agent chat pages
 * - Fixed positioning below header + breadcrumb
 * - Signal-based reactive state management
 * - Dual sidebar support (coexists with agent chat sidebar)
 *
 * Accessibility (WCAG 2.1 Level AA - Task 6):
 * - ARIA labels: All interactive elements have descriptive aria-labels
 * - Semantic landmarks: <nav> element for sidebar, <h2> section heading, <ul>/<li> for recent chats
 * - ARIA live regions: Loading/error states use aria-live for screen reader announcements
 * - Keyboard navigation: Full keyboard support (Tab, Enter, Space)
 * - Focus indicators: 2px solid green outline on all focusable elements
 * - Color contrast: All text meets 4.5:1 ratio (normal text), 3:1 ratio (UI components)
 * - Touch targets: Minimum 44x44px for all interactive elements (mobile compliance)
 * - Screen reader: Tested with VoiceOver (macOS/iOS) and NVDA (Windows)
 * - Semantic HTML: Proper heading hierarchy, button elements, list structures
 */
@Component({
  selector: 'app-workspace-sidebar',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    WorkspaceRecentChatItemComponent,
    IconComponent,
  ],
  templateUrl: './workspace-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './workspace-sidebar.component.scss',
})
export class WorkspaceSidebarComponent {
  private readonly sessionsNamespace = 'workspace-sidebar';
  private readonly router = inject(Router);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly sidebarStateService = inject(WorkspaceSidebarStateService);
  private readonly workspaceSessionsStateService = inject(
    WorkspaceSessionsStateService,
  );
  private readonly dialog = inject(MatDialog);
  private readonly viewportService = inject(ViewportService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly navContextService = inject(NavigationContextService);
  private readonly appScrollService = inject(AppScrollService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public isExpanded = computed(() => this.sidebarStateService.isExpanded());
  public isMobile = computed(() => this.viewportService.isMobile());

  /**
   * Sidebar width based on expanded state and route context.
   * On chat pages (mobile): hidden (workspace nav lives inside chat sidebar).
   * On workspace/catalog pages (mobile): toggled via hamburger.
   * Desktop: always shows, collapsed or expanded based on route.
   */
  public sidebarWidth = computed(() => {
    const onChatRoute = this.navContextService.isChatContext();
    if (this.isMobile()) {
      if (onChatRoute) return SIDEBAR_WIDTH_STYLES.NONE;
      return this.isExpanded()
        ? `${SIDEBAR_WIDTHS.SEARCH_EXPANDED}px`
        : SIDEBAR_WIDTH_STYLES.NONE;
    }
    return this.isExpanded()
      ? SIDEBAR_WIDTH_STYLES.EXPANDED
      : SIDEBAR_WIDTH_STYLES.COLLAPSED;
  });

  /**
   * Sidebar visibility — driven entirely by NavigationContextService.
   * Visible on all workspace-scoped routes (workspace home, settings, chat on desktop).
   */
  public showSidebar = computed(() => {
    const ctx = this.navContextService.navContext();
    if (ctx === null || ctx === 'system') return false;
    if (this.isMobile() && ctx === 'chat') return false;
    return true;
  });

  /**
   * Active nav item derived purely from navigation context — no URL matching.
   *
   * 'chat'               → 'chats'    (recent-chats section highlighted)
   * 'workspace-settings' → 'settings' (settings nav item highlighted)
   * 'workspace'          → 'home'     (agent catalog highlighted)
   * anything else        → null
   */
  public activeItem = computed(() => {
    const ctx = this.navContextService.navContext();
    if (ctx === 'chat') return 'chats';
    if (ctx === 'workspace-recent-chats') return 'chats';
    if (ctx === 'workspace-settings') return 'settings';
    if (ctx === 'workspace') return 'home';
    return null;
  });

  public recentChats = this.workspaceSessionsStateService.sessionsBy(
    this.sessionsNamespace,
  );
  public recentChatsLoading = this.workspaceSessionsStateService.loadingBy(
    this.sessionsNamespace,
  );
  public recentChatsSearching = this.workspaceSessionsStateService.searchingBy(
    this.sessionsNamespace,
  );
  public recentChatsError = computed(
    () =>
      !!this.workspaceSessionsStateService.errorBy(this.sessionsNamespace)(),
  );
  public recentChatsStateSearchQuery =
    this.workspaceSessionsStateService.searchQueryBy(this.sessionsNamespace);
  private readonly recentChatsLoaded = signal<boolean>(false);
  private readonly lastLoadedWorkspaceId = signal<string | null>(null);
  private readonly previousExpanded = signal<boolean>(false);

  public searchQuery = signal<string>('');
  private readonly debouncedSearchQuery = signal<string>('');
  private readonly sidebarSearch$ = new Subject<string>();

  public filteredRecentChats = computed(() => this.recentChats());

  public showSidebarSearchInput = computed(() => {
    const hasChats = this.recentChats().length > 0;
    const hasTypedQuery = this.searchQuery().trim().length > 0;
    const hasPendingDebouncedQuery = this.debouncedSearchQuery().length > 0;
    const hasServiceDebouncedQuery =
      this.recentChatsStateSearchQuery().trim().length > 0;

    return (
      hasChats ||
      hasTypedQuery ||
      hasPendingDebouncedQuery ||
      hasServiceDebouncedQuery ||
      this.recentChatsLoading() ||
      this.recentChatsSearching()
    );
  });

  public groupedRecentChats = computed(() => {
    const chats = this.filteredRecentChats();
    return groupByTimePeriod(chats, (chat) => chat.lastMessageTimestamp);
  });

  public chatGroupEntries = computed(() => {
    const groups = this.groupedRecentChats();
    return [
      { key: 'today', labelKey: 'SESSION.GROUP_TODAY', items: groups.today },
      {
        key: 'yesterday',
        labelKey: 'SESSION.GROUP_YESTERDAY',
        items: groups.yesterday,
      },
      {
        key: 'last7Days',
        labelKey: 'SESSION.GROUP_LAST_7_DAYS',
        items: groups.last7Days,
      },
      {
        key: 'last30Days',
        labelKey: 'SESSION.GROUP_LAST_30_DAYS',
        items: groups.last30Days,
      },
      { key: 'older', labelKey: 'SESSION.GROUP_OLDER', items: groups.older },
    ];
  });

  /**
   * Whether the mobile backdrop should be shown.
   * Backdrop appears only on workspace pages, not chat pages where sidebars coexist.
   */
  public shouldShowBackdrop = computed(() => {
    return (
      this.isMobile() &&
      this.isExpanded() &&
      !this.navContextService.isChatContext()
    );
  });

  constructor() {
    // Forward search to service — debounce is handled by searchSessions$ in WorkspaceSessionsStateService.
    this.sidebarSearch$.pipe(takeUntilDestroyed()).subscribe((query) => {
      const normalizedQuery = query.trim();
      this.debouncedSearchQuery.set(normalizedQuery);

      const workspaceId = this.getWorkspaceId();
      if (!workspaceId || !this.isExpanded()) {
        return;
      }

      this.workspaceSessionsStateService.searchSessions$.next({
        query: normalizedQuery,
        subscriptionId: workspaceId,
        namespace: this.sessionsNamespace,
      });
    });

    // Sync sidebar width to shared service whenever it changes
    effect(() => {
      const widthStr = this.sidebarWidth();
      const widthNum = parseInt(widthStr, 10);
      const expanded = this.isExpanded();
      this.sidebarStateService.setSidebarState(widthNum, expanded);
    });

    /**
     * Unified route + viewport expansion effect.
     *
     * Rules:
     * - Chat route OR mobile → collapse (icon-only or hidden)
     * - Non-chat desktop → expand (full labels)
     *
     * This single effect replaces both the previous NavigationEnd subscription
     * (setExpansionByRoute) and the separate viewport-change effect. The
     * NavigationContextService's own NavigationEnd subscription drives navContext,
     * which this effect reacts to reactively.
     */
    effect(() => {
      const isChatRoute = this.navContextService.isChatContext();
      const isMobile = this.isMobile();
      if (isChatRoute || isMobile) {
        this.sidebarStateService.isExpanded.set(false);
      } else {
        this.sidebarStateService.isExpanded.set(true);
      }
    });

    // Lazy load recent chats when sidebar expands or workspace changes
    effect(() => {
      const expanded = this.isExpanded();
      const workspaceId = this.getWorkspaceId();
      const lastWorkspaceId = this.lastLoadedWorkspaceId();
      const wasExpanded = this.previousExpanded();

      if (expanded && workspaceId) {
        const workspaceChanged = workspaceId !== lastWorkspaceId;
        const justExpanded = !wasExpanded;
        if (workspaceChanged || justExpanded) {
          this.loadRecentChats(
            workspaceId,
            this.searchQuery().trim() || undefined,
          );
        }
      }
      this.previousExpanded.set(expanded);
    });
  }

  private getWorkspaceId(): string | null {
    const fromService = this.workspaceService.currentSubscriptionId();
    if (fromService) return fromService;
    const url = this.router.url || '';
    const query = url.split('?')[1] || '';
    return new URLSearchParams(query).get('subscription_id');
  }

  public closeSidebar(): void {
    if (this.isMobile() && this.isExpanded()) {
      this.sidebarStateService.setSidebarState(
        parseInt(this.sidebarWidth(), 10),
        false,
      );
    }
  }

  // Document-wide Escape dismissal — the mobile backdrop is aria-hidden and
  // non-focusable, so a template-level keydown binding alone is unreachable.
  @HostListener('document:keydown.escape')
  public onDocumentEscape(): void {
    this.closeSidebar();
  }

  public toggleSidebar(): void {
    this.sidebarStateService.toggleSidebar();
  }

  public navigateToHome(): void {
    const workspaceId = this.getWorkspaceId();
    if (workspaceId) {
      this.router.navigate(['/agents'], {
        queryParams: { subscription_id: workspaceId },
      });
    } else {
      console.warn('[WorkspaceSidebar] Cannot navigate: No workspace selected');
      this.showNoWorkspaceSelectedSnackbar();
    }
  }

  public navigateToSettings(): void {
    const workspaceId = this.getWorkspaceId();
    if (!workspaceId) {
      console.warn('[WorkspaceSidebar] Cannot navigate: No workspace selected');
      this.showNoWorkspaceSelectedSnackbar();
      return;
    }

    this.workspaceService.selectWorkspace$.next(workspaceId);
    this.router.navigate(['/workspaces/settings'], {
      queryParams: { subscription_id: workspaceId },
      state: { returnUrl: this.router.url },
    });
  }

  public onViewAllChats(): void {
    const subscriptionId =
      this.workspaceService.getSubscriptionIdForNavigation();
    const navigation = this.router.navigate(['/workspaces/recent-chats'], {
      queryParams: subscriptionId ? { subscription_id: subscriptionId } : {},
    });

    // Force top anchor behavior even when router treats it as same-URL navigation.
    void Promise.resolve(navigation).then(() => this.scrollRecentChatsToTop());
  }

  private scrollRecentChatsToTop(): void {
    this.appScrollService.scrollToTop('auto');
  }

  /**
   * Load recent chats from API
   * Task 4.3: Lazy loading implementation
   *
   * @param workspaceId - Workspace identifier
   * @param search
   */
  private loadRecentChats(workspaceId: string, search?: string): void {
    this.workspaceSessionsStateService.fetchSessions$.next({
      subscriptionId: workspaceId,
      page: 1,
      pageSize: SIDEBAR_MAX_CHATS,
      search,
      append: false,
      namespace: this.sessionsNamespace,
    });

    this.recentChatsLoaded.set(true);
    this.lastLoadedWorkspaceId.set(workspaceId);
  }

  /**
   * Navigate to chat session
   * Task 4.5: Click handler for chat navigation
   *
   * @param chat - Recent chat item
   */
  public navigateToChat(chat: RecentChat): void {
    const workspaceId = this.getWorkspaceId();
    if (workspaceId) {
      // Use agentSystemName for routing (e.g., 'task_decomposition_agent')
      // agentName is the display name (e.g., 'Task Navigator') for UI only
      this.router.navigate(['/agents', chat.agentSystemName, chat.sessionId], {
        queryParams: { subscription_id: workspaceId },
      });
    }
  }

  /**
   * Handle chat rename
   * Updates session metadata via API
   *
   * @param payload - Contains chat and new name
   */
  public onChatRename(payload: { chat: RecentChat; newName: string }): void {
    const { chat, newName } = payload;

    this.workspaceSessionsStateService
      .renameSession({
        session: chat,
        newName,
        namespace: this.sessionsNamespace,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {},
        error: (error) => {
          this.snackBar.open(
            extractErrorMessage(
              error,
              'WORKSPACE.RECENT_CHATS.TOAST.RENAME_ERROR',
            ),
            this.translate.instant('COMMON.DISMISS'),
            {
              duration: 5000,
            },
          );
        },
      });
  }

  /**
   * Handle chat delete
   * Opens Material dialog for confirmation, then deletes session via API
   *
   * @param chat - Recent chat item to delete
   */
  public onChatDelete(chat: RecentChat): void {
    // Open confirmation dialog (same as chat sidebar)
    const dialogRef = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(
      ConfirmationDialogComponent,
      deleteChatDialogConfig(
        chat.sessionName ||
          this.translate.instant('WORKSPACE.RECENT_CHATS.UNTITLED'),
      ),
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.workspaceSessionsStateService
          .deleteSession(chat)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {},
            error: (error) => {
              this.snackBar.open(
                extractErrorMessage(
                  error,
                  'WORKSPACE.RECENT_CHATS.TOAST.DELETE_ERROR',
                ),
                this.translate.instant('COMMON.DISMISS'),
                {
                  duration: 5000,
                },
              );
            },
          });
      });
  }

  public clearSearch(): void {
    this.onSidebarSearchChange('');
  }

  public onSidebarSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.sidebarSearch$.next(value);
  }

  private showNoWorkspaceSelectedSnackbar(): void {
    this.snackBar.open(
      this.translate.instant('WORKSPACE_SIDEBAR.NO_WORKSPACE_SELECTED'),
      this.translate.instant('COMMON.DISMISS'),
      {
        duration: 3000,
      },
    );
  }
}
